import Clipboard from "clipboard";
import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Button, StyledProps as StyledButtonProps } from "../Button";
import { ConditionalWrapper } from "../ConditionalWrapper";
import { Icon } from "../Icon";
import { Tooltip } from "../Tooltip";
import { style } from "../style";

// ms to show copied state
const COPIED_DURATION = 2000;
// default icon to show
const ICON_DEFAULT = "copy";
// icon to show when showing copied state
const ICON_COPIED = "check";

type StyledCopyButtonProps = {
  isCopying?: boolean;
};

/*
  override the button styles and the small and icon css classes
  raise the z-index so the focus outline goes above the gradient
*/
export const ClipboardButton = styled(Button)<StyledCopyButtonProps>`
  ${({ isCopying }) =>
    isCopying &&
    css`
      cursor: default;
    `}
`;

export const CopyIcon = styled(Icon)<StyledCopyButtonProps>`
  color: ${style.colors.grey.dark};

  ${({ isCopying }) =>
    !isCopying &&
    css`
      ${ClipboardButton}:hover & {
        color: ${style.colors.blue.dark};
      }
    `}
`;

export type CopyButtonProps = {
  copy: string;
  onClick?: () => void;
  noTooltip?: boolean;
  tooltipText?: string;
  ariaLabel?: string;
  className?: string;
};

export const CopyButton: React.FC<CopyButtonProps & StyledButtonProps> = ({
  copy,
  onClick,
  noTooltip,
  tooltipText = "Copy",
  ariaLabel = "Copy to clipboard",
  className,
  variation = "tertiary",
  compact,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isCopying, setIsCopying] = useState(false);
  const [btnIcon, setBtnIcon] = useState(ICON_DEFAULT);

  const onClickButton = (): void => {
    if (onClick) {
      onClick();
    }

    if (!isCopying) {
      setIsCopying(true);
      setBtnIcon(ICON_COPIED);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isCopying) {
      timer = setTimeout(() => {
        setBtnIcon(ICON_DEFAULT);
        setIsCopying(false);
        clearTimeout(timer);
      }, COPIED_DURATION);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [isCopying]);

  useEffect(() => {
    if (buttonRef.current) {
      const clickToCopyClipboard = new Clipboard(buttonRef.current, {
        text: () => copy,
      });

      return () => {
        clickToCopyClipboard.destroy();
      };
    }
  }, [buttonRef.current, copy]);

  return (
    <ConditionalWrapper
      condition={!noTooltip}
      wrapper={(children) => (
        <Tooltip content={tooltipText}>
          <div>{children}</div>
        </Tooltip>
      )}
    >
      <ClipboardButton
        ref={buttonRef}
        type="button"
        variation={variation}
        compact={compact}
        isCopying={isCopying}
        aria-label={ariaLabel}
        onClick={onClickButton}
        className={className}
      >
        <CopyIcon icon={btnIcon} isCopying={isCopying} />
      </ClipboardButton>
    </ConditionalWrapper>
  );
};
